/**
 * 「キーを 1 つ入れるだけの小さな画面」（/enter-key/:projectId/:env?name=NAME・pages/EnterKeyPage.tsx）の純粋な判断。
 * 2026-10-04 creator/chat-secrets（founder「バイブコーダーは API という言葉や、シークレットキーという言葉を知らない前提です」
 *   「できればバイブコーダーがすべてチャットで完結できるといいんですけどね」）。
 *
 * どこから開くか: AI（MCP の functions_secret_set・apps/mcp/src/tools/secretInput.ts の secretEntryPageUrl）が、URL モードの
 *   elicitation でチャットの中のボタンとして出す。名前は AI が決めて ?name= に入れる（名前は値ではない）。本人は貼って「保存する」を押すだけ。
 *   MCP は本体の一覧の更新時刻で保存を見つけ、チャットに「保存しました」を返す = 本人は保存の後チャットに戻るだけ。
 * WHY Functions の画面（FunctionSecretsCard）へのリンクにしなかったか: あの画面は名前の欄・一覧・上書き・削除・環境の切り替えがあり、
 *   「どこに何を入れるか」を探させる。ここは欄 1 つとボタン 1 つだけ（入れる先はリンクで決まっている）。
 *
 * 値の扱いは FunctionSecretsCard と同じ決まり（URL に入れない・ログに出さない・送ったら消す・本番は確認を挟む = functionSecrets.ts）。
 * 依存なし（node --test を型の除去だけで回すため）。
 */
import { secretFromSearch } from './functionSecrets.ts';

/** App.tsx のルート。MCP の secretEntryPageUrl と同じ形（enterKey.test.ts が MCP のソースと突き合わせる） */
export const ENTER_KEY_PATH = '/enter-key/:projectId/:env';

export type EnterKeyEnv = 'development' | 'production';

export interface EnterKeyTarget {
  projectId: string;
  env: EnterKeyEnv;
  name: string;
  /** 何のサービスのキーか（AI が書く・例 "OpenAI"）。無い・決まりに合わない = null（見出しは名前で言う） */
  service: string | null;
  /** どこでもらった・何で始まる文字列か（AI が書く・例「OpenAI のサイトでもらった sk- で始まる文字列」）。無い・合わない = null */
  hint: string | null;
}

/**
 * service / hint の長さの上限（文字）。MCP の functions_secret_set の入力の上限と同じ（enterKey.test.ts が MCP のソースと突き合わせる）。
 * WHY 短く: 見出しと 1 行の説明に収まる長さ（スマホの幅で 2〜3 行）。長い文は「説明」ではなく指示や値が混ざりやすい
 */
export const KEY_SERVICE_MAX = 40;
export const KEY_HINT_MAX = 120;

/**
 * service / hint に使ってよい文か（2026-10-04 creator/chat-secrets-2・管理「何のキーかが名前を読まないとわからない」）。
 * この文は AI（= 騙されうるモデル）が書いて URL に入れる = この画面の見出しに出す前に、悪用の形を落とす:
 *   - 制御文字・< >（画面は React がテキストとして描く = HTML にはならないが、見た目を崩す物は最初から通さない）
 *   - リンク（://・www.）: 「このサイトでキーを作って」と別のサイトへ誘う道にしない
 *   - 製品名（{product}・kumo）: 「KUMODeck のパスワードを貼って」= ログインのパスワードを聞き出す見出しにしない
 *   - 24 文字以上続く英数字: キーそのもの（値）を説明に入れて URL・履歴に残すのを防ぐ（sk- のような頭だけは通る）
 * MCP の secretInput.ts keyLabelIssue と同じ決まり（MCP は合わなければ断る = モデルが直す。画面は黙って使わない = 名前で言う）
 */
export function keyLabelOk(s: string, max: number, productName: string): boolean {
  const v = s.trim();
  if (!v || [...v].length > max) return false;
  if (/[\u0000-\u001f\u007f<>]/.test(v)) return false;
  if (/:\/\/|www\./i.test(v)) return false;
  if (/kumo/i.test(v) || (productName && v.toLowerCase().includes(productName.toLowerCase()))) return false;
  if (/[A-Za-z0-9_\-]{24,}/.test(v)) return false;
  return true;
}

/**
 * URL から入れる先を読む。どれかがおかしければ null（画面は「リンクが正しくありません」）。
 * 名前の決まりは Functions の画面と同じ（secretFromSearch = 本体の BINDING_NAME_RE・KUMO_ は不可）。
 * ?name= を読む（Functions の画面の ?secret= と別の名前 = この画面の URL だと分かる）
 */
export function readEnterKey(params: { projectId?: string; env?: string }, search: string, productName = ''): EnterKeyTarget | null {
  const projectId = params.projectId ?? '';
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(projectId)) return null;
  if (params.env !== 'development' && params.env !== 'production') return null;
  const q = new URLSearchParams(search);
  const raw = q.get('name');
  const name = raw ? secretFromSearch(`?secret=${encodeURIComponent(raw)}`) : null;
  if (!name) return null;
  // service / hint は添え物: 合わなければ使わない（リンクは壊さない = 名前の見出しで入れられる）
  const label = (k: string, max: number) => {
    const v = q.get(k);
    return v !== null && keyLabelOk(v, max, productName) ? v.trim() : null;
  };
  return { projectId, env: params.env, name, service: label('service', KEY_SERVICE_MAX), hint: label('hint', KEY_HINT_MAX) };
}

/**
 * 本体のエラー → この画面の言い方。Functions の画面の言い方（fnSecret.error.*）は「上の『オンにする』を押して」など
 * あの画面のボタンを指すので、ここでは使わない（この画面にはボタンが無い = AI に頼む言い方）。null = 共通の errorText
 */
export function enterKeyErrorKey(code: string): string | null {
  switch (code) {
    case 'functions_not_deployed':
      return 'enterKey.error.notReady';
    case 'functions_disabled':
      return 'enterKey.error.stopped';
    case 'rate_limited':
      return 'enterKey.error.rateLimited';
    default:
      return null;
  }
}
